import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const STATS = [
  { value: '1,200m', label: 'Altitude' },
  { value: '4.9', label: 'Guest Rating' },
  { value: '120+', label: 'Happy Guests' },
  { value: '365', label: 'Days Open' },
];

const AMENITIES = [
  { title: 'Mountain Views', desc: 'Panoramic vistas of the Carpathian peaks stretch endlessly from every window and terrace.' },
  { title: 'Heated Pool', desc: 'Warm waters beneath open skies — bathe under the stars in any season of the year.' },
  { title: 'Fireplace', desc: 'Crackling flames and warm amber light create the perfect setting for unforgettable evenings.' },
  { title: 'Forest Trails', desc: 'Step outside into ancient woodland paths waiting to be explored at dawn or dusk.' },
  { title: 'Local Cuisine', desc: 'Traditional Romanian dishes prepared with fresh ingredients sourced from the surrounding valleys.' },
  { title: 'Stargazing', desc: 'Zero light pollution reveals a breathtaking canopy of stars visible from the observation deck.' },
];

const EXPERIENCES = [
  'Wildlife encounters in pristine forests',
  'Mushroom foraging with local guides',
  'Skiing at nearby slopes in winter',
  'Golden sunrise meditation sessions',
  'Traditional cooking workshops',
  'Mountain stream fishing excursions',
];

export default function HomePage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('[data-reveal]');
    if (!reveals.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('revealed'); obs.unobserve(e.target); } });
    }, { threshold: 0.15 });
    reveals.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="home page-enter">
      {/* ═══ HERO ═══ */}
      <section className="home__hero">
        {/* Background layers */}
        <div className="home__hero-bg">
          <div className="home__hero-gradient" />
          <div className="home__hero-pattern" />
          <div className="home__hero-mist home__hero-mist--1" />
          <div className="home__hero-mist home__hero-mist--2" />
        </div>

        {/* Animated floating particles */}
        <div className="home__hero-particles">
          {[...Array(20)].map((_, i) => (
            <span key={i} className="home__hero-particle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${6 + Math.random() * 8}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
              opacity: 0.15 + Math.random() * 0.3,
            }} />
          ))}
        </div>

        {/* Mountain silhouettes */}
        <div className="home__hero-mountains">
          <svg className="home__hero-mountain home__hero-mountain--back" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path d="M0,320 L0,200 Q120,80 240,160 Q360,60 480,140 Q600,40 720,120 Q840,20 960,100 Q1080,30 1200,110 Q1320,50 1440,130 L1440,320 Z" />
          </svg>
          <svg className="home__hero-mountain home__hero-mountain--mid" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path d="M0,320 L0,220 Q180,120 360,200 Q480,100 600,180 Q720,80 840,170 Q960,90 1080,160 Q1200,100 1440,180 L1440,320 Z" />
          </svg>
          <svg className="home__hero-mountain home__hero-mountain--front" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path d="M0,320 L0,260 Q200,180 400,240 Q550,170 700,230 Q850,160 1000,220 Q1150,170 1300,210 Q1380,200 1440,240 L1440,320 Z" />
          </svg>
        </div>

        {/* Aurora / light bands */}
        <div className="home__hero-aurora">
          <div className="home__hero-aurora-band home__hero-aurora-band--1" />
          <div className="home__hero-aurora-band home__hero-aurora-band--2" />
          <div className="home__hero-aurora-band home__hero-aurora-band--3" />
        </div>

        {/* Decorative leaf shapes */}
        <div className="home__hero-leaf home__hero-leaf--1" />
        <div className="home__hero-leaf home__hero-leaf--2" />
        <div className="home__hero-leaf home__hero-leaf--3" />

        {/* Main content */}
        <div className="home__hero-content" data-reveal>
          {/* Tagline */}
          <div className="home__hero-tagline">
            <span className="home__hero-tagline-line" />
            <span className="home__hero-tagline-text">A Carpathian Sanctuary</span>
            <span className="home__hero-tagline-line" />
          </div>

          {/* Title */}
          <h1 className="home__hero-title">
            <span className="home__hero-title-main">Cabana</span>
            <span className="home__hero-title-accent">BURU</span>
          </h1>

          {/* Ornamental line */}
          <div className="home__hero-ornament">
            <span className="home__hero-ornament-wing home__hero-ornament-wing--left" />
            <span className="home__hero-ornament-diamond" />
            <span className="home__hero-ornament-wing home__hero-ornament-wing--right" />
          </div>

          {/* Subtitle */}
          <p className="home__hero-subtitle">
            Nestled at 1,200 m in ancient forests where<br />
            time surrenders to the rhythm of nature
          </p>

          {/* Info chips */}
          <div className="home__hero-chips">
            <span className="home__hero-chip">Romania</span>
            <span className="home__hero-chip-sep" />
            <span className="home__hero-chip">Mountain Retreat</span>
            <span className="home__hero-chip-sep" />
            <span className="home__hero-chip">Est. 1947</span>
          </div>

          {/* Buttons — vertical stack */}
          <div className="home__hero-actions">
            <Link to="/rent" className="home__hero-cta">
              <span className="home__hero-cta-text">Book Your Stay</span>
              <span className="home__hero-cta-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </span>
            </Link>
            <Link to="/gallery" className="home__hero-link">
              View Gallery
              <span className="home__hero-link-line" />
            </Link>
          </div>

          {/* Guest rating */}
          <div className="home__hero-rating">
            <div className="home__hero-rating-stars">★★★★★</div>
            <span className="home__hero-rating-text">4.9 from 120+ guests</span>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="home__hero-scroll">
          <div className="home__hero-scroll-mouse">
            <span className="home__hero-scroll-wheel" />
          </div>
          <span className="home__hero-scroll-text">Scroll to discover</span>
          <span className="home__hero-scroll-chevrons">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="6 9 12 15 18 9"/></svg>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="6 9 12 15 18 9"/></svg>
          </span>
        </div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <section className="home__stats">
        <div className="home__stats-inner">
          {STATS.map((s, i) => (
            <div key={i} className="home__stat" data-reveal style={{ transitionDelay: `${i * 0.1}s` }}>
              <span className="home__stat-value">{s.value}</span>
              <span className="home__stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ STORY ═══ */}
      <section className="home__story">
        <div className="home__story-inner">
          <div className="home__section-label" data-reveal>
            <span className="home__section-label-line" />
            <span>Our Story</span>
            <span className="home__section-label-line" />
          </div>
          <h2 className="home__story-title" data-reveal>
            Where Mountains<br /><em>Meet Magic</em>
          </h2>
          <div className="home__story-cols" data-reveal>
            <p className="home__story-text">
              Perched at 1,200 meters in the Romanian Carpathians, Cabana Buru has welcomed
              travelers since generations past. Built from ancient spruce and local stone,
              every beam tells a story of mountain craftsmanship passed down through centuries.
            </p>
            <p className="home__story-text">
              Whether you seek adventure on rugged trails or serenity by a crackling fire,
              this is the place where your story continues. The forest breathes, the mountains
              watch, and time moves at a different pace.
            </p>
          </div>
          <div className="home__ornament" data-reveal>
            <span className="home__ornament-line" />
            <span className="home__ornament-diamond" />
            <span className="home__ornament-line" />
          </div>
        </div>
      </section>

      {/* ═══ AMENITIES ═══ */}
      <section className="home__amenities">
        <div className="home__amenities-inner">
          <div className="home__section-label" data-reveal>
            <span className="home__section-label-line" />
            <span>Amenities</span>
            <span className="home__section-label-line" />
          </div>
          <h2 className="home__amenities-title" data-reveal>
            Mountain<br /><em>Luxuries</em>
          </h2>
          <div className="home__amenities-grid">
            {AMENITIES.map((a, i) => (
              <div key={i} className="home__amenity" data-reveal style={{ transitionDelay: `${i * 0.08}s` }}>
                <span className="home__amenity-number">{String(i + 1).padStart(2, '0')}</span>
                <div className="home__amenity-body">
                  <h3 className="home__amenity-title">{a.title}</h3>
                  <p className="home__amenity-desc">{a.desc}</p>
                </div>
                <span className="home__amenity-line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ EXPERIENCE ═══ */}
      <section className="home__experience">
        <div className="home__experience-inner">
          <div className="home__experience-left" data-reveal="left">
            <div className="home__experience-visual">
              <div className="home__experience-visual-card" />
              <div className="home__experience-visual-accent" />
            </div>
          </div>
          <div className="home__experience-right" data-reveal="right">
            <div className="home__section-label home__section-label--light">
              <span className="home__section-label-line" />
              <span>The Experience</span>
            </div>
            <h2 className="home__experience-title">
              Immerse Yourself<br />in <em>Wild Beauty</em>
            </h2>
            <p className="home__experience-desc">
              Wake up to birdsong. Feel the cool mountain air. Watch deer graze in the
              meadow below while mist rolls through the valley. At Cabana Buru, nature
              isn't a backdrop — it's the main event.
            </p>
            <ul className="home__experience-list">
              {EXPERIENCES.map((exp, i) => (
                <li key={i} className="home__experience-item">
                  <span className="home__experience-item-dot" />
                  <span>{exp}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIAL ═══ */}
      <section className="home__testimonial">
        <div className="home__testimonial-inner" data-reveal>
          <div className="home__testimonial-quote">"</div>
          <blockquote className="home__testimonial-text">
            Cabana Buru was the most magical place we've ever stayed. The mountains, the warmth,
            the crackling fire — it felt like stepping into a fairy tale. We're already planning
            our return.
          </blockquote>
          <div className="home__testimonial-author">
            <span className="home__testimonial-line" />
            <div>
              <span className="home__testimonial-name">Maria & Andrei</span>
              <span className="home__testimonial-origin">Bucharest, Romania</span>
            </div>
          </div>
          <div className="home__testimonial-stars">
            <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="home__cta">
        <div className="home__cta-inner" data-reveal>
          <div className="home__cta-deco">
            <span /><span /><span />
          </div>
          <h2 className="home__cta-title">
            Ready for your<br /><em>mountain escape?</em>
          </h2>
          <p className="home__cta-desc">
            Reserve your dates and let the Carpathians do the rest.
          </p>
          <Link to="/rent" className="home__cta-btn">
            <span>Book Now</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </Link>
        </div>
      </section>

    </div>
  );
}